import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { RotateCcw, TrendingUp, AlertTriangle, ChevronDown, Sigma, Info } from "lucide-react";
import { Reveal, EASE } from "./shared";
import { LeadDialog } from "./LeadDialog";
import { fmtMil } from "./MethodFlow";
import { SIM_DEFAULTS, fmtFull, fmtPct, fmtPct2, fmtImc } from "./simulatorMath";

const SLIDERS = [
  { key: "custo", label: "Custo do lote", min: 5000, max: 60000, step: 1000, format: "money" },
  { key: "receitaProj", label: "Receita projetada do lote", min: 10000, max: 100000, step: 1000, format: "money" },
  { key: "despesasFixas", label: "Despesas fixas do mês", min: 5000, max: 80000, step: 1000, format: "money" },
  { key: "receitaReal", label: "Receita realizada até agora", min: 0, max: 120000, step: 1000, format: "money" },
  { key: "despVar", label: "Despesas variáveis sobre vendas", min: 0, max: 40, step: 1, format: "percent", hint: "taxas de cartão, comissões, impostos, marketplace…", wide: true },
];

const fmtSliderValue = (s, v) => (s.format === "percent" ? `${v}%` : fmtMil(v));

const Tip = ({ text }) => (
  <span className="group/tip relative inline-flex" tabIndex={0} aria-label={text}>
    <Info className="h-3.5 w-3.5 cursor-help text-tria-subtle transition-colors group-hover/tip:text-tria-sky" />
    <span
      role="tooltip"
      className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-2 w-56 -translate-x-1/2 rounded-xl border border-white/10 bg-tria-navy px-3.5 py-2.5 text-left text-[11px] font-normal normal-case leading-relaxed tracking-normal text-tria-mist opacity-0 shadow-xl transition-opacity duration-200 group-hover/tip:opacity-100 group-focus-within/tip:opacity-100"
    >
      {text}
    </span>
  </span>
);

const TONES = {
  amber: { box: "border-amber-400/30 bg-amber-400/[0.06]", icon: "text-amber-400", title: "text-amber-300" },
  emerald: { box: "border-emerald-400/30 bg-emerald-400/[0.07]", icon: "text-emerald-400", title: "text-emerald-300" },
  rose: { box: "border-rose-400/30 bg-rose-400/[0.06]", icon: "text-rose-400", title: "text-rose-300" },
};

const mensagemCenario = (c) => {
  switch (c.status) {
    case "sem_margem":
      return {
        tone: "rose",
        title: "A receita projetada não gera margem suficiente para sustentar este cenário.",
        lines: ["O custo do lote é igual ou superior à receita projetada. Revise o preço ou o custo antes de projetar o faturamento necessário."],
      };
    case "inviavel":
      return {
        tone: "rose",
        title: "Não há ponto de equilíbrio calculável neste cenário.",
        lines: ["A margem de contribuição estimada é igual ou inferior a zero. Antes de projetar o faturamento necessário, reveja preço, custo do lote ou despesas variáveis."],
      };
    case "equilibrio":
      return {
        tone: "emerald",
        title: "Ponto de equilíbrio atingido.",
        lines: ["Nas premissas desta simulação, a margem de contribuição gerada é suficiente para cobrir as despesas fixas."],
      };
    case "acima":
      return {
        tone: "emerald",
        title: "Ponto de equilíbrio atingido.",
        lines: [
          `Nas premissas desta simulação, sua operação ultrapassou o ponto de equilíbrio em ${fmtMil(c.excedente)} de faturamento.`,
          `Resultado operacional estimado: ${fmtMil(c.resultado)}.`,
        ],
      };
    default:
      return {
        tone: "amber",
        title: "Cenário abaixo do ponto de equilíbrio.",
        lines: [
          `Mantendo uma margem de contribuição estimada de ${fmtPct(c.imcPct)}, seu ponto de equilíbrio é ${fmtMil(c.pe)}. Com ${fmtMil(c.receitaReal)} realizados, ainda seriam necessários aproximadamente ${fmtMil(c.falta)} em vendas para atingir o equilíbrio.`,
          "A TRIA ajuda você a entender o impacto de preços, custos, despesas variáveis, margem e metas de vendas antes de tomar uma decisão.",
        ],
      };
  }
};

const MemStep = ({ n, title, lines, testId }) => (
  <div data-testid={testId} className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
    <p className="font-mono text-[10px] font-bold tracking-[0.2em] text-tria-blue">{n}. {title}</p>
    <div className="mt-2.5 space-y-1 font-mono text-[12px] leading-relaxed text-tria-mist">
      {lines.map((l, i) => (
        <p key={i} className={l.startsWith("=") || l.startsWith("≈") ? "font-bold text-white" : ""}>{l}</p>
      ))}
    </div>
  </div>
);

const FLOW_CHIPS = [
  "Receita Projetada", "Custo da Mercadoria", "Margem Bruta",
  "Despesas Variáveis", "Margem de Contribuição", "Despesas Fixas",
  "Ponto de Equilíbrio e Resultado",
];

const MemoryCalc = ({ c }) => (
  <div className="space-y-3 pt-5">
    <div>
      <p className="font-mono text-[10px] font-bold tracking-[0.25em] text-tria-sky">MEMÓRIA DE CÁLCULO</p>
      <p className="mt-1 text-[12px] text-tria-subtle">Veja como a TRIA chegou aos resultados desta simulação.</p>
    </div>

    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4" data-testid="sim-memory-premises">
      <p className="font-mono text-[10px] font-bold tracking-[0.2em] text-tria-blue">PREMISSAS DO CENÁRIO</p>
      <div className="mt-2.5 grid grid-cols-2 gap-x-6 gap-y-1.5 font-mono text-[12px] sm:grid-cols-3">
        <p className="text-tria-mist">Custo do lote: <span className="text-white">{fmtFull(c.custo)}</span></p>
        <p className="text-tria-mist">Receita projetada: <span className="text-white">{fmtFull(c.receitaProj)}</span></p>
        <p className="text-tria-mist">Despesas variáveis: <span className="text-white">{fmtPct(c.despVar, 0)}</span></p>
        <p className="text-tria-mist">Despesas fixas: <span className="text-white">{fmtFull(c.despesasFixas)}</span></p>
        <p className="text-tria-mist">Receita realizada: <span className="text-white">{fmtFull(c.receitaReal)}</span></p>
      </div>
    </div>

    <MemStep n={1} title="MARGEM BRUTA PROJETADA" testId="sim-memory-step-1" lines={[
      "Receita Projetada − Custo do Lote",
      `${fmtFull(c.receitaProj)} − ${fmtFull(c.custo)}`,
      `= ${fmtFull(c.margemBruta)}`,
      `Margem Bruta % = ${fmtFull(c.margemBruta)} ÷ ${fmtFull(c.receitaProj)}`,
      `= ${fmtPct2(c.margemBrutaPct)}`,
    ]} />

    <MemStep n={2} title="MARGEM DE CONTRIBUIÇÃO ESTIMADA" testId="sim-memory-step-2" lines={[
      "Margem Bruta % − Despesas Variáveis %",
      `${fmtPct2(c.margemBrutaPct)} − ${fmtPct(c.despVar, 0)}`,
      `= ${fmtPct2(c.imcPct)}`,
    ]} />

    <MemStep n={3} title="MARGEM DE CONTRIBUIÇÃO ACUMULADA" testId="sim-memory-step-3" lines={[
      "Receita Realizada × IMC",
      `${fmtFull(c.receitaReal)} × ${fmtPct2(c.imcPct)}`,
      `≈ ${fmtFull(c.mcAcumulada)}`,
    ]} />

    <MemStep n={4} title="PONTO DE EQUILÍBRIO" testId="sim-memory-step-4" lines={
      c.pe != null
        ? ["Despesas Fixas ÷ IMC", `${fmtFull(c.despesasFixas)} ÷ ${fmtImc(c.imc)}`, `≈ ${fmtFull(c.pe)}`]
        : ["Não calculável — margem de contribuição estimada igual ou inferior a zero."]
    } />

    <MemStep n={5} title="FATURAMENTO ADICIONAL NECESSÁRIO" testId="sim-memory-step-5" lines={
      c.pe != null
        ? ["Ponto de Equilíbrio − Receita Realizada", `${fmtFull(c.pe)} − ${fmtFull(c.receitaReal)}`, `≈ ${fmtFull(c.falta)}`]
        : ["Não aplicável neste cenário."]
    } />

    <MemStep n={6} title="RESULTADO OPERACIONAL ESTIMADO" testId="sim-memory-step-6" lines={[
      "Margem de Contribuição Acumulada − Despesas Fixas",
      `${fmtFull(c.mcAcumulada)} − ${fmtFull(c.despesasFixas)}`,
      `≈ ${fmtFull(c.resultado)}`,
    ]} />

    <div className="flex flex-wrap items-center gap-1.5 rounded-xl border border-white/[0.06] bg-white/[0.02] p-4" data-testid="sim-memory-flow">
      {FLOW_CHIPS.map((chip, i) => (
        <span key={chip} className="flex items-center gap-1.5">
          <span className="rounded-full border border-tria-sky/20 bg-tria-sky/[0.05] px-2.5 py-1 font-mono text-[9.5px] font-semibold text-tria-mist">{chip}</span>
          {i < FLOW_CHIPS.length - 1 && <span className="text-[10px] text-tria-blue">↓</span>}
        </span>
      ))}
    </div>

    <div className="space-y-1.5 px-1 pb-1">
      <p className="text-[11px] leading-relaxed text-tria-subtle">
        Esta simulação utiliza a margem projetada do lote como referência para estimar o comportamento econômico do
        cenário. Na operação real, a TRIA utiliza os dados efetivamente registrados de vendas, custos e despesas para
        calcular os indicadores gerenciais.
      </p>
      <p className="text-[11px] leading-relaxed text-tria-subtle">
        Os resultados apresentados são estimativas gerenciais e não substituem demonstrações contábeis, fiscais ou
        financeiras oficiais.
      </p>
    </div>
  </div>
);

export const FlowSimulator = ({ sim, setSim, calc }) => {
  const [memOpen, setMemOpen] = useState(false);
  const msg = mensagemCenario(calc);
  const tone = TONES[msg.tone];
  const BannerIcon = msg.tone === "emerald" ? TrendingUp : AlertTriangle;

  const cards = [
    { id: "margem-bruta", label: "MARGEM BRUTA PROJETADA", value: fmtPct(calc.margemBrutaPct), cls: "text-white", tip: "Quanto permanece da receita projetada após o custo das mercadorias, antes das demais despesas." },
    { id: "imc", label: "MARGEM DE CONTRIBUIÇÃO ESTIMADA", value: fmtPct(calc.imcPct), cls: calc.imcPct > 0 ? "text-tria-sky" : "text-rose-400", tip: "Quanto da receita permanece após os custos das mercadorias e as despesas variáveis para ajudar a pagar a estrutura fixa e, depois, formar resultado." },
    { id: "pe", label: "PONTO DE EQUILÍBRIO", value: calc.pe != null ? fmtMil(calc.pe) : "—", cls: "text-white", tip: "Faturamento estimado necessário para que a margem de contribuição seja suficiente para cobrir as despesas fixas." },
    { id: "resultado", label: "RESULTADO ESTIMADO ATUAL", value: fmtMil(calc.resultado), cls: calc.resultado >= 0 ? "text-emerald-400" : "text-rose-400", tip: "Margem de contribuição acumulada menos as despesas fixas consideradas na simulação." },
  ];

  return (
    <Reveal>
      <div data-testid="flow-simulator" className="glass-card mb-6 rounded-3xl p-6 sm:p-8">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-tria-sky">Simulador do método</p>
            <h3 className="mt-1.5 font-display text-lg font-semibold text-white">Simule e veja como margem, custos e faturamento se conectam</h3>
          </div>
          <button
            data-testid="sim-reset-btn"
            onClick={() => setSim(SIM_DEFAULTS)}
            className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-2 text-[12px] font-semibold text-tria-mist transition-all hover:border-tria-sky/50 hover:text-white"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Restaurar
          </button>
        </div>

        <div className="grid gap-x-8 gap-y-5 sm:grid-cols-2">
          {SLIDERS.map((s) => {
            const pct = ((sim[s.key] - s.min) / (s.max - s.min)) * 100;
            return (
              <div key={s.key} className={s.wide ? "sm:col-span-2" : ""}>
                <div className="mb-2 flex items-center justify-between gap-3">
                  <label htmlFor={`sim-${s.key}`} className="text-[12.5px] font-medium text-tria-mist">
                    {s.label}
                    {s.hint && <span className="ml-2 hidden text-[10.5px] text-tria-subtle sm:inline">{s.hint}</span>}
                  </label>
                  <span className="font-mono text-[13px] font-bold text-white" data-testid={`sim-value-${s.key}`}>{fmtSliderValue(s, sim[s.key])}</span>
                </div>
                <input
                  id={`sim-${s.key}`}
                  data-testid={`sim-slider-${s.key}`}
                  type="range"
                  min={s.min}
                  max={s.max}
                  step={s.step}
                  value={sim[s.key]}
                  aria-valuemin={s.min}
                  aria-valuemax={s.max}
                  aria-valuenow={sim[s.key]}
                  aria-label={s.label}
                  onChange={(e) => setSim((prev) => ({ ...prev, [s.key]: Number(e.target.value) }))}
                  className="tria-range w-full"
                  style={{ "--fill": `${pct}%` }}
                />
              </div>
            );
          })}
        </div>

        <div className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="sim-cards">
          {cards.map((card) => (
            <div key={card.id} data-testid={`sim-card-${card.id}`} className="rounded-xl border border-white/[0.07] bg-white/[0.02] p-4">
              <p className="flex items-center gap-1.5 text-[9.5px] font-bold uppercase tracking-[0.12em] text-tria-subtle">
                {card.label}
                <Tip text={card.tip} />
              </p>
              <p className={`mt-2 font-mono text-lg font-bold sm:text-xl ${card.cls}`}>{card.value}</p>
            </div>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={calc.status}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.35 }}
            data-testid="sim-result"
            className={`mt-5 flex items-start gap-3 rounded-2xl border px-5 py-4 ${tone.box}`}
          >
            <BannerIcon className={`mt-0.5 h-5 w-5 shrink-0 ${tone.icon}`} />
            <div>
              <p className={`text-[13.5px] font-bold leading-relaxed ${tone.title}`}>{msg.title}</p>
              {msg.lines.map((l, i) => (
                <p key={i} className="mt-1 text-[13px] leading-relaxed text-white/85">{l}</p>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>

        <div className="mt-5 flex flex-col items-center justify-between gap-3 border-t border-white/[0.07] pt-5 sm:flex-row">
          <p className="text-center text-[12.5px] leading-relaxed text-tria-subtle sm:text-left">
            Gostou do cenário? A equipe envia a simulação em PDF e mostra como aplicar o método no seu negócio.
          </p>
          <LeadDialog
            sim={calc}
            trigger={
              <button
                data-testid="sim-save-lead-btn"
                className="inline-flex shrink-0 items-center gap-2 rounded-full border border-tria-sky/40 px-5 py-2.5 text-[13px] font-bold text-tria-sky transition-all duration-300 hover:bg-tria-sky/10 hover:text-white"
              >
                Receber minha simulação
              </button>
            }
          />
        </div>

        <div className="mt-4 border-t border-white/[0.07] pt-4">
          <button
            data-testid="sim-memory-toggle"
            onClick={() => setMemOpen(!memOpen)}
            aria-expanded={memOpen}
            aria-controls="sim-memory"
            className="flex w-full items-center justify-between text-[12.5px] font-semibold text-tria-mist transition-colors hover:text-white"
          >
            <span className="inline-flex items-center gap-2">
              <Sigma className="h-4 w-4 text-tria-sky" />
              Ver memória de cálculo
            </span>
            <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${memOpen ? "rotate-180" : ""}`} />
          </button>
          <AnimatePresence initial={false}>
            {memOpen && (
              <motion.div
                id="sim-memory"
                data-testid="sim-memory"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.4, ease: EASE }}
                className="overflow-hidden"
              >
                <MemoryCalc c={calc} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </Reveal>
  );
};
